<template>
  <div class="energy-stats-table-wrapper">
    <v-table
      class="energy-stats-table"
      density="comfortable"
      hover
    >
      <thead>
        <tr>
          <th class="energy-source-header">
            <span class="table-header-label">
              <v-icon size="18">
                mdi-lightning-bolt-outline
              </v-icon>

              {{ energySourcesText }}
            </span>
          </th>

          <th class="text-right">
            <span class="column-heading">
              {{ energyCostLabel }}
              <small>€/a</small>
            </span>
          </th>

          <th class="text-right">
            <span class="column-heading">
              {{ energyConsumptionLabel }}
              <small>kWh/a</small>
            </span>
          </th>

          <th class="text-right">
            <span class="column-heading">
              {{ energyPriceLabel }}
              <small>€/kWh</small>
            </span>
          </th>
        </tr>
      </thead>

      <tbody>
        <EnergyStatsYearFuel
          v-for="(fuel, index) in fuels"
          :key="fuel.id"
          :fuel="fuel"
          :model-value="modelValue[fuel.id] || {}"
          :errors="errors[index] || {}"
          :hint="hintFuel?.[fuel.name] || ''"
          :feed="feed"
          @update:model-value="updateFuel(fuel.id, $event)"
        />
      </tbody>
    </v-table>

    <div
      v-if="!fuels.length"
      class="energy-table-empty-state"
    >
      <v-icon size="36" color="primary">
        mdi-database-off-outline
      </v-icon>

      <div class="font-weight-bold mt-3">
        Keine Energieträger verfügbar
      </div>

      <div class="text-body-2 text-medium-emphasis mt-1">
        Für dieses Projekt stehen derzeit keine Energiedaten zur Verfügung.
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import EnergyStatsYearFuel from '@/components/project/EnergyStatsYearFuel.vue'

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },

  fuels: {
    type: Array,
    required: true,
  },

  errors: {
    type: Array,
    default: () => [],
  },

  hintFuel: {
    type: Object,
    default: () => ({}),
  },

  feed: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['update:modelValue'])

const energySourcesText = computed(() => {
  return props.feed
    ? 'Einspeisequelle'
    : 'Energieträger'
})

const energyCostLabel = computed(() => {
  return props.feed
    ? 'Einspeiseumsatz'
    : 'Energiekosten'
})

const energyConsumptionLabel = computed(() => {
  return props.feed
    ? 'Einspeiseenergie'
    : 'Energieverbrauch'
})

const energyPriceLabel = computed(() => {
  return props.feed
    ? 'Einspeisevergütung'
    : 'Energiepreis'
})

function updateFuel(fuelId, newValue) {
  const updatedData = {
    ...props.modelValue,
  }

  updatedData[fuelId] = newValue

  emit('update:modelValue', updatedData)
}
</script>

<style scoped lang="scss">
.energy-stats-table-wrapper {
  position: relative;

  width: 100%;
  margin-top: 1.25rem;

  overflow-x: auto;

  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 22px;

  background-color: rgb(var(--v-theme-surface));

  scrollbar-width: thin;
}

.energy-stats-table {
  min-width: 760px;
  background-color: transparent;
}

.energy-stats-table :deep(table) {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.energy-stats-table :deep(thead th) {
  padding: 0.85rem 0.9rem;

  color: rgb(var(--v-theme-primary));
  background:
    linear-gradient(
      rgba(var(--v-theme-primary), 0.055),
      rgba(var(--v-theme-primary), 0.055)
    ),
    rgb(var(--v-theme-surface));

  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.3;
  white-space: nowrap;

  border-bottom: 1px solid
    rgba(var(--v-theme-primary), 0.14);
}

.energy-source-header {
  min-width: 190px;
}

.table-header-label {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.column-heading {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.1rem;
}

.column-heading small {
  color: rgba(var(--v-theme-on-surface), 0.58);

  font-size: 0.7rem;
  font-weight: 500;
}

/* Empty state */
.energy-table-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;

  min-height: 240px;
  padding: 2rem 1rem;

  text-align: center;
}

@media (max-width: 700px) {
  .energy-stats-table-wrapper {
    border-radius: 18px;
  }

  .energy-stats-table {
    min-width: 700px;
  }
}
</style>